import { AxiosError } from 'axios'
import { useCallback, useState } from 'react'
import { ACCOUNT_ROLES } from '../../../../constants/endpoints'
import api from '../../../../repository/api'
import { ApiError } from '../../../../types/api-error.type'

export type GrantableRole = 'profile_user' | 'organizer'

/** 'granted' — the role was just added. 'already-granted' — a 409, the account already had it. */
export type GrantRoleResult = 'granted' | 'already-granted' | 'failed'

/**
 * POST /odin/account/roles — grants a role to the caller's own account.
 * The target account comes from the JWT, so there is nothing to pass but the role.
 */
export function useGrantRole() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ApiError | undefined>()
  const [result, setResult] = useState<GrantRoleResult | undefined>()

  const grantRole = useCallback(async (role: GrantableRole): Promise<GrantRoleResult> => {
    setError(undefined)
    setIsLoading(true)
    setResult(undefined)

    try {
      await api.post(ACCOUNT_ROLES, { role })
      setResult('granted')
      return 'granted'
    } catch (err) {
      if (!(err instanceof AxiosError)) {
        setError({ message: String(err) } as ApiError)
        setResult('failed')
        return 'failed'
      }

      const status = err.response?.status

      // 409 isn't a failure to report: the account already holds the role, so
      // there is nothing to grant and nothing for the user to fix.
      if (status === 409) {
        setResult('already-granted')
        return 'already-granted'
      }

      // The 401 from the auth middleware is plain text, not the JSON envelope
      // every other status uses, so only trust response.data when it's an object.
      const data = err.response?.data
      setError(
        typeof data === 'object' && data !== null
          ? (data as ApiError)
          : ({ code: status, message: err.message } as ApiError),
      )
      setResult('failed')
      return 'failed'
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { grantRole, isLoading, error, result }
}
